<script setup>
// Сторінка прямого ефіру: плеєр Mux ліворуч, чат Twitch праворуч.
// Зі спільного сайту не тягне нічого зайвого — ні пінгів онлайну, ні
// навбара (див. layouts/live.vue). Єдиний запит до Strapi — перевірка
// доступу: звідти ж приходить і Playback ID трансляції.
definePageMeta({
  layout: 'live',
  pageTransition: false
})

const config = useRuntimeConfig()
const route = useRoute()

const {
  access,
  pending: accessPending,
  failed: accessFailed,
  refresh: refreshAccess
} = useLiveAccess()

// Ефір вимкнено тумблером в адмінці — сторінка поводиться так, ніби стріму
// немає: ні плеєра, ні гейта, просто «офлайн».
// Локально замок знято разом із цим тумблером (див. useLiveAccess), тож
// екран офлайну відкривається рукою: /live?offline=1
const offline = computed(() => {
  if (import.meta.dev && route.query.offline) return route.query.offline !== '0'
  return !!access.value.offline
})

// Запис щойно закінченого ефіру: ще дві години після вимкнення бекенд віддає
// джерело окремим полем replay (див. access у live-setting). Глядач вмикає
// його кнопкою на екрані офлайну — сам плеєр без спитку не підіймаємо
const replayOpen = ref(false)
const replay = computed(() => (offline.value ? access.value.replay || null : null))
const watchingReplay = computed(() => replayOpen.value && !!replay.value)

// Ефір обмежений, а глядач під обмеження не підходить — замість плеєра
// екран входу. Поки доступ не перевірено, плеєр теж не малюємо
const blocked = computed(
  () => accessPending.value || accessFailed.value || (access.value.restricted && !access.value.allowed)
)

// Playback ID приходить зі Strapi (поле в «Прямий ефір»). Фолбеки —
// MUX_PLAYBACK_ID зі збірки і ?id=<playbackId> для тестів; обидва працюють
// лише після того, як доступ підтверджено.
//
// Вимкнений ефір гасить і фолбеки: бекенд свій streamId уже не віддає, але
// без цієї перевірки плеєр однаково піднявся б на MUX_PLAYBACK_ID зі збірки
const playbackId = computed(() => {
  if (blocked.value || offline.value) return ''
  return access.value.streamId || route.query.id || config.public.muxPlaybackId || ''
})

// Ефір зі свого сервера приходить готовим посиланням — беремо як є, бо це
// довільний .m3u8, а не playback ID, з якого треба збирати адресу
const src = computed(() => {
  if (watchingReplay.value && !blocked.value) return replay.value.url
  if (blocked.value || offline.value) return ''
  // Локальна розробка. Бекенд віддає справжнє посилання на ефір лише тим,
  // кому дозволено дивитись, тож на localhost його немає навіть із знятим
  // замком — джерело вказуємо самі: разово через ?src=<плейлист> або
  // назавжди через LIVE_DEV_SRC у .env. Обидва працюють ТІЛЬКИ в dev, щоб
  // посиланням не можна було підмінити ефір глядачам
  if (import.meta.dev) {
    const devSrc = route.query.src || config.public.liveDevSrc
    if (devSrc) return String(devSrc)
  }
  if (access.value.testMode && access.value.testUrl) return access.value.testUrl
  return playbackId.value ? `https://stream.mux.com/${playbackId.value}.m3u8` : ''
})

// Який чат показувати поруч із плеєром — вирішує адмінка (Strapi → Прямий
// ефір → «Чат»). Локально відповідь бекенда не приїжджає через CORS, тож у
// dev вибір можна задати рукою: /live?chat=live
const chatSource = computed(() => {
  if (import.meta.dev && route.query.chat) return String(route.query.chat)
  return access.value.chatSource || 'twitch'
})

// Чий чат Twitch показуємо. Зазвичай свій, але ефір може йти з чужого
// каналу — тоді канал вказують в адмінці. Локально — /live?tw=канал
const chatTwitch = computed(() => {
  if (import.meta.dev && route.query.tw) return String(route.query.tw)
  return access.value.chatTwitch || 'olsior'
})

// Гілка власного чату під цей ефір. Порожньо в адмінці = спільна гілка
// «live», тобто чат не обнуляється між стрімами
const chatPageId = computed(() => {
  if (import.meta.dev && route.query.thread) return String(route.query.thread)
  return access.value.chatPageId || 'live'
})

const chatOpen = ref(true)
const chatMode = ref('panel')
const chatWidth = ref(26)

// Тривалість ефіру беремо з Twitch через наш /api/twitchStream: Helix
// вимагає app access token, який не можна світити на клієнті. Опитуємо раз
// на 60 с, а відповідь на сервері кешується на 20 с, тож навантаження не
// залежить від онлайну.
//
// Лічильник глядачів Twitch у шапці свідомо не показуємо: у ній лишається
// тільки наш власний онлайн, щоб дві різні цифри поруч не плутали
const { startedAt } = useTwitchStreamStatus()

// Наші глядачі — через той самий механізм онлайну, що й на сторінках аніме,
// тільки під зарезервованим слагом. Побічно ці сесії враховуються і в
// загальному онлайні сайту
const presence = useOnlinePresence(LIVE_PRESENCE_SLUG)
const liveViewers = computed(() => presence.value?.anime || 0)

// Кнопка «Чат» живе разом із панеллю керування плеєра: коли та ховається
// (миша пішла з плеєра / простій), кнопка теж має зникати, інакше вона
// висить над відео весь час
const controlsVisible = ref(true)

// Корінь сторінки — саме його плеєр розгортає на весь екран, щоб у фулскрін
// потрапив і чат. pseudoFullscreen вмикається там, де Fullscreen API немає
const rootRef = ref()

// --- Кліпи з ефіру ---
// Редактор висить знизу поверх сторінки: /live — це повноекранна розкладка
// «плеєр + чат», і місця під плеєром там немає. Відео при цьому лишається
// видно — прев'ю відрізка грає саме воно
const playerRef = ref(null)
const clipOpen = ref(false)

const openClip = async () => {
  await playerRef.value?.exitFullscreen?.()
  clipOpen.value = true
}

const getClipVideo = () => playerRef.value?.getVideo?.()

const getClipFragments = () => {
  const hls = playerRef.value?.getHls?.()
  const fragments = fragmentsFromHls(hls)
  const level = hls?.levels?.[hls.currentLevel >= 0 ? hls.currentLevel : hls.loadLevel]
  if (level && !level.height) {
    throw new ClipError('Зараз грає «Тільки звук» — увімкніть якість з відео, щоб зробити кліп')
  }
  if (!fragments?.length) {
    throw new ClipError('У цьому браузері ефір грає без hls.js — кліп з ефіру тут не зробити. Спробуйте Chrome чи Firefox')
  }
  return fragments
}

const clipSource = computed(() => ({
  sourceType: 'live',
  label: `Прямий ефір${streamTitle.value ? ` · ${streamTitle.value}` : ''}`,
  title: ''
}))
const pseudoFullscreen = ref(false)

// Плеєр займає всю ширину, коли чат закритий або винесений у плаваюче вікно
const chatTakesSpace = computed(() => chatOpen.value && chatMode.value === 'panel')

const toggleChat = () => {
  chatOpen.value = !chatOpen.value
}

// Дані для прев'ю посилань (Telegram, Discord) — саме серверним запитом:
// краулери JS не виконують, тож клієнтський useLiveAccess для og-тегів не
// годиться. Беруться лише публічні поля картки, вердикт доступу не чіпаємо.
const strapiClient = useStrapiClient()
const { data: liveMeta } = await useAsyncData('live-meta', async () => {
  try {
    return await strapiClient('/live-setting/access')
  } catch (e) {
    // Без прев'ю жити можна, без сторінки — ні
    return null
  }
})

// Назва ефіру для шапки над відео. Беремо з серверної відповіді (вона є
// одразу, ще до клієнтської перевірки доступу), а клієнтську лишаємо як
// свіжішу — назву в адмінці могли змінити просто під час ефіру
const streamTitle = computed(
  () => access.value.title || liveMeta.value?.title || ''
)

// Заголовок вкладки — за тим самим правилом: серверна відповідь як основа
// (краулерам і на першому рендері більше нічого немає), клієнтська як
// свіжіша. Клієнтську ми ще й перепитуємо (див. useLiveAccess), тож
// перейменований під час ефіру стрім змінює і назву вкладки
const pageTitle = computed(
  () => access.value.title || liveMeta.value?.title || 'Olsior — прямий ефір'
)
const pageDescription = computed(() =>
  liveMeta.value?.title
    ? `${liveMeta.value.title} — прямий ефір Olsior.`
    : 'Прямий ефір Olsior: плеєр і чат Twitch на одній сторінці.'
)

// og:image має бути абсолютним, а Strapi віддає відносний шлях.
//
// Домен беремо з ВЖЕ отриманого config, а не через useStrapiMedia. Саме
// той виклик і клав сторінку в 500: unhead обчислює значення тегів ліниво,
// вже поза контекстом Nuxt, і композабл усередині computed падав з
// «[nuxt] instance unavailable». Тут звичайна конкатенація — падати нічому
const mediaBase = config.public.apiBase || 'https://admin.olsior.tv'
const ogImage = computed(() => {
  const url = liveMeta.value?.posterUrl
  if (!url) return 'https://anime.olsior.tv/site.jpg'
  return url.startsWith('http') ? url : `${mediaBase}${url}`
})

useHead({
  title: pageTitle,
  meta: [
    { name: 'description', content: pageDescription },
    // Сторінка існує для глядачів, а не для пошуку — індексувати нічого
    { name: 'robots', content: 'noindex' },

    { hid: 'og:title', property: 'og:title', content: pageTitle },
    {
      hid: 'og:description',
      property: 'og:description',
      content: pageDescription
    },
    { hid: 'og:image', property: 'og:image', content: ogImage },
    { hid: 'og:type', property: 'og:type', content: 'video.other' },
    {
      hid: 'og:url',
      property: 'og:url',
      content: 'https://anime.olsior.tv/live'
    },

    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription },
    { name: 'twitter:image', content: ogImage }
  ]
})
</script>

<template>
  <!--
    Цей корінь і йде у фулскрін — разом із чатом, а не сам плеєр. На iPhone,
    де Fullscreen API немає, плеєр вмикає pseudoFullscreen, і ми розтягуємо
    сторінку стилями: виглядає так само, тільки лишається панель Safari
  -->
  <div
    ref="rootRef"
    class="relative flex md:flex-row flex-col w-full h-full overflow-hidden"
    :class="
      pseudoFullscreen
        ? 'z-[100] fixed inset-0 bg-base-100 w-screen h-screen !flex-row'
        : ''
    "
  >
    <!-- Плеєр -->
    <div
      class="relative flex-1 bg-black min-w-0 h-full"
      :class="{
        'max-md:h-[45%] max-md:flex-none': chatTakesSpace && !pseudoFullscreen
      }"
    >
      <LivePlayer
        v-if="src"
        ref="playerRef"
        v-model:controls-visible="controlsVisible"
        :src="src"
        :chat-open="chatOpen"
        :started-at="watchingReplay ? null : startedAt"
        :title="streamTitle"
        v-model:pseudo-fullscreen="pseudoFullscreen"
        v-model:chat-width="chatWidth"
        :test-mode="access.testMode"
        :fullscreen-target="rootRef"
        @toggle-chat="toggleChat"
        @clip="openClip"
      />
      <div
        v-else-if="accessPending"
        class="flex justify-center items-center h-full"
      >
        <img
          src="/minloading.webp"
          class="rounded-full overflow-hidden"
          width="40"
          height="40"
          alt=""
        />
      </div>

      <!--
        Не змогли спитати бекенд — не відкриваємо ефір «про всяк випадок»,
        бо ми не знаємо, чи ввімкнені обмеження
      -->
      <div
        v-else-if="accessFailed"
        class="flex flex-col justify-center items-center gap-3 px-6 h-full text-center"
      >
        <p class="font-semibold text-lg">Не вдалося перевірити доступ</p>
        <button class="btn btn-primary btn-sm" @click="refreshAccess">
          Спробувати ще раз
        </button>
      </div>

      <!--
        Вимкнений ефір показуємо раніше за гейт: коли стріму немає, вимагати
        підписку немає сенсу — це той самий екран для всіх
      -->
      <LiveOffline
        v-else-if="offline"
        :access="access"
        @replay="replayOpen = true"
      />

      <LiveGate v-else-if="blocked" :access="access" />

      <div
        v-else
        class="flex flex-col justify-center items-center gap-2 px-6 h-full text-center"
      >
        <p class="font-semibold text-lg">Стрім не налаштований</p>
        <p class="opacity-60 text-sm">
          Вкажіть ID трансляції у Strapi → Прямий ефір (або MUX_PLAYBACK_ID
          у .env). Щоб стрімити зі свого сервера — там же ввімкніть
          «Свій сервер» і вкажіть URL потоку.
        </p>
      </div>

      <!--
        Шапка ефіру: назва і глядачі. Живе тут, а не в корені сторінки, щоб
        триматись правого краю САМЕ відео — інакше з відкритим чатом вона
        лягала б поверх чату. Ховається разом із панеллю керування, як і
        решта плаваючих елементів.
        pointer-events-none — щоб не з'їдати кліки по відео; кнопка чату
        всередині вмикає їх собі назад
      -->
      <Transition name="fade">
        <div
          v-if="controlsVisible"
          class="top-3 right-3 z-40 absolute flex items-start gap-2 max-w-[70%] pointer-events-none"
        >
          <!--
            Кожен показник — окрема плашка в ряд: назва і лічильник між
            собою не пов'язані, і в спільній коробці цифра читалась як
            підпис до назви
          -->
          <!--
            Висота у всіх плашок однакова (h-8, як у кнопки чату поруч), бо
            кегль тексту різний і по вмісту вони виходили різними
          -->
          <div
            v-if="streamTitle"
            class="flex items-center bg-neutral px-3 rounded-lg min-w-0 h-8 font-semibold text-neutral-content text-sm"
            :title="streamTitle"
          >
            <span class="truncate">{{ streamTitle }}</span>
          </div>

          <!--
            Показуємо завжди, навіть нуль: порожнє місце читається як
            «лічильник зламався», а чесний 0 — як «поки нікого»
          -->
          <div
            class="flex items-center gap-1 bg-neutral px-3 rounded-lg h-8 font-medium text-primary text-xs shrink-0"
            title="Дивляться на сайті"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {{ liveViewers.toLocaleString('uk-UA') }}
          </div>

          <!--
            Кнопка чату стоїть у тому ж рядку, що й шапка: обидві тримаються
            правого краю відео, і одна одну більше не перекриває
          -->
          <button
            v-if="!chatOpen"
            class="gap-2 btn btn-primary btn-sm pointer-events-auto shrink-0"
            title="Відкрити чат"
            @click="chatOpen = true"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 32 32"
            >
              <path
                fill="currentColor"
                d="M17.74 30L16 29l4-7h6a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h9v2H6a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v12a4 4 0 0 1-4 4h-4.84Z"
              ></path>
            </svg>
            Чат
          </button>
        </div>
      </Transition>
    </div>

    <!-- Чат: панель праворуч або плаваюче вікно -->
    <LiveChat
      :source="chatSource"
      :chat-page-id="chatPageId"
      :chat-twitch="chatTwitch"
      :chat-title="streamTitle || 'Чат ефіру'"
      :force-row="pseudoFullscreen"
      v-model:open="chatOpen"
      v-model:mode="chatMode"
      v-model:width="chatWidth"
    />

    <!--
      Обидві плаваючі кнопки живуть за тим самим правилом, що й панель
      керування плеєра: пішла миша з відео — зникли й вони, щоб нічого не
      висіло над картинкою. Коли плеєра немає (гейт, стрім не налаштований),
      controlsVisible лишається true, тож кнопки нікуди не діваються.
    -->
    <Transition name="fade">
      <!-- Теж <a>: повертаємось на сайт повним перезавантаженням -->
      <a
        v-if="controlsVisible"
        href="/"
        class="top-3 left-3 z-40 absolute gap-2 btn btn-neutral btn-sm"
        title="Повернутися на сайт"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M20 11H7.83l5.59-5.59L12 4l-8 8l8 8l1.41-1.41L7.83 13H20v-2z"
          ></path>
        </svg>
        На сайт
      </a>
    </Transition>

    <ClientOnly>
      <div
        v-if="clipOpen && src"
        class="right-0 bottom-0 left-0 z-[60] fixed mx-auto p-2 max-w-[1200px] max-h-[75vh] overflow-y-auto"
      >
        <LazyClipEditor
          :get-video="getClipVideo"
          :get-fragments="getClipFragments"
          :source="clipSource"
          live
          @close="clipOpen = false"
        />
      </div>
    </ClientOnly>
  </div>
</template>
